/**
 * jaiio-cart.js — ajout au panier et tiroir (T041, T042).
 *
 * Chargé par `sections/mini-cart.liquid`, donc sur toutes les pages. Focal
 * (`theme.js`, non modifié) garde l'ajout, le rendu et l'ouverture du tiroir ;
 * ce fichier ne fait que s'accrocher à ses événements.
 *
 * 1. Pièce ajoutée (desktop) : le tiroir s'ouvre avec la ligne sur fond ivoire.
 *    Focal remplace tout le contenu du tiroir à chaque rendu : c'est
 *    `<jaiio-cart-added>`, rendu dans le tiroir, qui repose la marque à chaque
 *    fois. Refermé, le tiroir redevient « plein » sans marque.
 * 2. Bandeau mobile (maquette « ajout mobile ») : sous 1000 px, sur la fiche,
 *    l'ajout ne recouvre pas la page — le tiroir ne s'ouvre pas, un bandeau
 *    `role="status"` le dit au-dessus de la barre d'achat, dont le bouton passe
 *    à « Dans le panier » (`jaiio-product-buy.js`). Le bandeau est rendu par
 *    la fiche (`snippets/product-added-toast.liquid`) seulement quand la barre
 *    existe : ailleurs, ou barre coupée, Focal ouvre le tiroir comme avant.
 * 3. Favoris du tiroir vide (`<jaiio-cart-favorites>`, T042) : lus dans Swish
 *    (`window.WishlistKing`, clientes connectées seulement) à l'ouverture du
 *    tiroir, quatre pièces disponibles ; « Ajouter » suit le même chemin que le
 *    formulaire produit de Focal. Swish absent, lent ou en erreur : le bloc
 *    reste masqué, le tiroir vide garde le reste.
 *
 * 4. Applis à shadow DOM dans le tiroir (case « C'est un cadeau ? » de Wrapped) :
 *    le piège de focus de Focal ne voit pas à l'intérieur d'un shadow root et
 *    prend un clic dans l'app pour un clic hors du tiroir, qui se ferme. Il est
 *    mis en pause le temps de l'interaction, repris dès qu'on revient ailleurs
 *    dans le tiroir ; Échap, qu'il n'entend plus, est pris ici. La même app émet ensuite `cart:refresh` sans panier : il
 *    est complété avant d'atteindre Focal.
 *
 * Erreur d'ajout (pièce partie entre-temps) : la notification globale de
 * Focal (`<cart-notification global>`), inchangée.
 */
(function () {
  'use strict';

  if (window.jaiioCart) return; // section rendue deux fois (éditeur)

  var TOAST_DELAY = 5000;
  var SWISH_TIMEOUT = 3000;
  var FAVORITES_COUNT = 4;
  var pocket = window.matchMedia('(max-width: 999px)');
  var addedVariant = null;
  var keepDrawerClosed = false;

  function drawer() {
    return document.getElementById('mini-cart');
  }

  // ---- 1. Ligne ajoutée ----------------------------------------------------

  class CartAdded extends HTMLElement {
    connectedCallback() {
      if (!addedVariant) return;
      var root = this.closest('cart-drawer');
      var line = root && root.querySelector('.cd-li[data-variant-id="' + addedVariant + '"]');
      if (line) line.classList.add('is-added');
    }
  }

  if (!window.customElements.get('jaiio-cart-added')) {
    window.customElements.define('jaiio-cart-added', CartAdded);
  }

  document.addEventListener('openable-element:close', function (event) {
    if (event.target !== drawer()) return;
    addedVariant = null;
    event.target.querySelectorAll('.cd-li.is-added').forEach(function (line) {
      line.classList.remove('is-added');
    });
  });

  // ---- 2. Bandeau mobile ---------------------------------------------------

  function toastFor(form) {
    // `getAttribute` et non `form.id` : le formulaire d'ajout porte un champ
    // nommé `id` (la variante), que `form.id` renverrait.
    var formId = form && form.getAttribute && form.getAttribute('id');
    if (!formId) return null;
    var toast = document.querySelector('[data-cart-toast="' + formId + '"]');
    // La barre d'achat peut être masquée (réglage, largeur) : sans elle, pas de
    // bandeau, et le tiroir s'ouvre.
    var bar = toast && toast.closest('.pp-bar');
    return bar && bar.getClientRects().length > 0 ? toast : null;
  }

  function hideToast(toast) {
    clearTimeout(toast._jaiioTimer);
    toast.querySelector('[data-cart-toast-body]').hidden = true;
  }

  function showToast(toast) {
    var body = toast.querySelector('[data-cart-toast-body]');
    body.hidden = false;
    clearTimeout(toast._jaiioTimer);
    toast._jaiioTimer = setTimeout(function () {
      // Laissé visible tant que la visiteuse le touche ou y a le focus.
      if (toast.matches(':hover, :focus-within')) return showToast(toast);
      hideToast(toast);
    }, TOAST_DELAY);
  }

  document.addEventListener('variant:added', function (event) {
    var variant = event.detail && event.detail.variant;
    var toast = pocket.matches ? toastFor(event.target) : null;

    keepDrawerClosed = !!toast;
    addedVariant = !toast && variant ? String(variant.variant_id || variant.id) : null;
    if (toast) showToast(toast);
  });

  // `cart:refresh` suit `variant:added` ; Focal lit `openMiniCart` au moment du
  // rendu. Écouté en capture sur `window`, donc avant le tiroir.
  window.addEventListener('cart:refresh', function (event) {
    if (!keepDrawerClosed || !event.detail || !event.detail.openMiniCart) return;
    event.detail.openMiniCart = false;
    keepDrawerClosed = false;
  }, true);

  // « Voir le panier » ouvre le tiroir (lien `toggle-link`) : le bandeau s'efface.
  document.addEventListener('openable-element:open', function (event) {
    if (event.target !== drawer()) return;
    document.querySelectorAll('[data-cart-toast]').forEach(hideToast);
  });

  // ---- 3. Favoris du tiroir vide -------------------------------------------

  function whenSwishReady() {
    return new Promise(function (resolve, reject) {
      if (window.WishlistKing) return resolve(window.WishlistKing);
      var timer = setTimeout(function () { reject(new Error('Swish indisponible')); }, SWISH_TIMEOUT);
      document.addEventListener('wk:app:ready', function () {
        clearTimeout(timer);
        resolve(window.WishlistKing);
      }, { once: true });
    });
  }

  // Ajout au panier depuis un bouton hors formulaire produit (favoris du tiroir,
  // page Ma wishlist). Même séquence que `ProductForm._onSubmit` (Focal) :
  // `variant:added`, puis `cart:updated` et `cart:refresh`, qui fait passer le
  // tiroir à l'état plein. Erreur (pièce partie) : la notification globale de
  // Focal. `aria-busy` sur le bouton pendant l'appel, et un second clic attend.
  async function addVariant(button, variantId) {
    if (button.getAttribute('aria-busy') === 'true') return false;
    var routes = (window.themeVariables && window.themeVariables.routes) || {};
    button.setAttribute('aria-busy', 'true');
    try {
      var response = await fetch((routes.cartAddUrl || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: 1, sections: ['mini-cart'] })
      });
      var added = await response.json();
      if (!response.ok) {
        button.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true, cancelable: true, detail: { status: 'error', error: added.description || '' }
        }));
        return false;
      }
      button.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: added } }));
      var cart = await (await fetch((routes.cartUrl || '/cart') + '.js')).json();
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      return true;
    } catch (error) {
      console.warn('[jaiio-cart] ajout impossible :', error);
      return false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // Partagé avec la page Ma wishlist (`assets/jaiio-wishlist.js`) : une seule
  // attente de Swish, un seul chemin d'ajout au panier.
  window.jaiioCart = { swishReady: whenSwishReady, addVariant: addVariant };

  // Même rendu que `snippets/product-item-price.liquid` : format de la boutique
  // (balises retirées), sans « ,00 » quand le thème les masque.
  function formatMoney(cents, format, trimZeros) {
    var token = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    var decimals = token.indexOf('no_decimals') !== -1 ? 0 : 2;
    var thousand = ',', decimal = '.';
    if (token.indexOf('comma_separator') !== -1) { thousand = '.'; decimal = ','; }
    if (token.indexOf('space_separator') !== -1) { thousand = ' '; decimal = ','; }
    var parts = (cents / 100).toFixed(decimals).split('.');
    var amount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
    if (parts[1] && !(trimZeros && parts[1] === '00')) amount += decimal + parts[1];
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }

  class CartFavorites extends HTMLElement {
    connectedCallback() {
      this._onOpen = this._onOpen.bind(this);
      this.addEventListener('click', this._onClick.bind(this));
      // Le tiroir est re-rendu à chaque page vue : on ne lit Swish qu'à l'ouverture.
      if (drawer() && drawer().open) this._load();
      else document.addEventListener('openable-element:open', this._onOpen);
    }

    disconnectedCallback() {
      document.removeEventListener('openable-element:open', this._onOpen);
    }

    _onOpen(event) {
      if (event.target !== drawer()) return;
      document.removeEventListener('openable-element:open', this._onOpen);
      this._load();
    }

    async _load() {
      var items;
      try {
        var app = await whenSwishReady();
        if (!app.isLoggedIn) return;
        var result = await app.apis.wishlist.loadWishlist({ wishlistId: 'mine' });
        items = (result && result.wishlist && result.wishlist.items) || [];
      } catch (error) {
        console.warn('[jaiio-cart] favoris non chargés :', error);
        return;
      }
      // Plus récents d'abord, comme la page des favoris de Swish.
      var cards = items.slice().reverse().map(function (item) {
        var product = item.product || {};
        var variants = product.variants || [];
        var variant = variants.find(function (v) { return v.id === item.variantId; })
          || variants.find(function (v) { return v.available; });
        return variant && variant.available ? { product: product, variant: variant } : null;
      }).filter(Boolean).slice(0, FAVORITES_COUNT);
      if (cards.length === 0 || !this.isConnected) return;
      this._render(cards);
    }

    _render(cards) {
      var list = this.querySelector('[data-favorites-list]');
      var template = this.querySelector('[data-favorites-card]');
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      var format = this.getAttribute('data-money-format') || settings.moneyFormat || '{{amount}}';
      var trimZeros = this.hasAttribute('data-trim-zeros');

      cards.forEach(function (card) {
        var node = template.content.firstElementChild.cloneNode(true);
        var image = (card.variant.featured_image && card.variant.featured_image.src) || card.product.featured_image;
        node.querySelectorAll('[data-card-link]').forEach(function (link) { link.href = card.product.url; });
        if (image) {
          var img = node.querySelector('[data-card-img]');
          img.src = image + (image.indexOf('?') === -1 ? '?' : '&') + 'width=264';
        }
        node.querySelector('[data-card-brand]').textContent = card.product.vendor || '';
        node.querySelector('[data-card-title]').textContent = card.product.title || '';
        node.querySelector('[data-card-price]').textContent = formatMoney(Math.round(card.variant.price), format, trimZeros);
        node.querySelector('[data-card-add]').setAttribute('data-variant-id', card.variant.id);
        list.appendChild(node);
      });
      this.hidden = false;
    }

    async _onClick(event) {
      var button = event.target.closest('[data-card-add]');
      if (button) addVariant(button, Number(button.getAttribute('data-variant-id')));
    }
  }

  // ---- 4. Applis à shadow DOM dans le tiroir -------------------------------

  // Même test que `focus-trap` (`theme.js`, `getActualTarget` puis `contains`) :
  // vrai si l'événement vient de l'intérieur d'un shadow root ouvert posé dans
  // le tiroir, faux s'il vient du reste du tiroir, null hors du tiroir.
  function fromShadowApp(event) {
    var root = drawer();
    if (!root || !root.open) return null;
    var path = event.composedPath();
    for (var i = 0; i < path.length; i++) {
      if (path[i] === root) return false;
      if (path[i].shadowRoot && root.contains(path[i])) return true;
    }
    return null;
  }

  // En capture sur `window`, donc avant les écouteurs du piège sur `document` :
  // en pause, ceux-ci sont retirés avant d'être appelés.
  ['pointerdown', 'mousedown', 'touchstart', 'focusin'].forEach(function (type) {
    window.addEventListener(type, function (event) {
      var inApp = fromShadowApp(event);
      if (inApp === null) return;
      var trap = drawer().focusTrap;
      if (!trap) return;
      if (inApp) trap.pause();
      else trap.unpause();
    }, true);
  });

  // Piège en pause, Échap n'est plus entendu par Focal : au clavier, le tiroir ne
  // se refermait plus une fois le focus sur la case cadeau (recette T044).
  window.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || fromShadowApp(event) !== true) return;
    event.preventDefault();
    drawer().open = false;
  }, true);

  // Après l'ajout du pochon cadeau, Wrapped émet `cart:refresh` sans `detail`.
  // Le tiroir de Focal s'en accommode (il relit sa section), pas le compteur de
  // l'en-tête (`CartCount` lit `detail.cart` et lève une erreur). L'événement est
  // arrêté, le panier relu, puis `cart:updated` et `cart:refresh` réémis complets,
  // dans l'ordre de Focal.
  var passThrough = false;
  window.addEventListener('cart:refresh', function (event) {
    if (event.detail || passThrough) return;
    event.stopImmediatePropagation();
    var routes = (window.themeVariables && window.themeVariables.routes) || {};
    var emit = function (name, detail) {
      document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    };
    fetch((routes.cartUrl || '/cart') + '.js', { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)); })
      .then(function (cart) {
        emit('cart:updated', { cart: cart });
        emit('cart:refresh', { cart: cart });
      })
      .catch(function (error) {
        // Panier illisible : l'événement repart tel quel, le tiroir se rafraîchit
        // au moins lui-même.
        console.warn('[jaiio-cart] panier non relu :', error);
        passThrough = true;
        emit('cart:refresh', null);
        passThrough = false;
      });
  }, true);

  if (!window.customElements.get('jaiio-cart-favorites')) {
    window.customElements.define('jaiio-cart-favorites', CartFavorites);
  }
})();
